import React, { useRef, useState } from "react";
import { motion } from "framer-motion";
import { X, Mail, Send, ArrowLeft, Paperclip, Loader2, Trash2 } from "lucide-react";
import adminApi from "./adminApi";

const MAX_MB = 5;
const ALLOWED_EXT = [".pdf", ".jpg", ".jpeg", ".png", ".doc", ".docx", ".xls", ".xlsx"];

const inputCls =
  "w-full border border-slate-300 rounded-lg px-3 py-2 text-sm text-slate-800 outline-none " +
  "focus:ring-2 focus:ring-[#0B2545]/20 focus:border-[#0B2545] transition placeholder:text-slate-400";
const readOnlyCls = `${inputCls} bg-slate-50 text-slate-600 cursor-not-allowed`;

const Row = ({ label, required, error, children }) => (
  <div className="grid grid-cols-1 sm:grid-cols-[150px_1fr] gap-x-4 gap-y-1 items-start">
    <label className="text-[13px] font-medium text-slate-700 sm:pt-2">
      {label}
      {required && <span className="text-red-500"> *</span>}
    </label>
    <div>
      {children}
      {error && <p className="text-[11px] text-red-500 mt-1">{error}</p>}
    </div>
  </div>
);

/**
 * Props
 *  student    : row from the list (needs reg_id, st_name, st_mailid)
 *  senderMail : Company Email ID from tbl_parameters (parid = 2), served by /meta
 *  onClose    : () => void
 *  onSent     : (message) => void   (called after a successful send)
 */
export default function SendMailModal({ student, senderMail, onClose, onSent }) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [file, setFile] = useState(null);
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);
  const [serverError, setServerError] = useState("");
  const fileRef = useRef(null);

  const receiver = (student.st_mailid || "").trim();
  const sender = (senderMail || "").trim();

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;

    const ext = "." + f.name.split(".").pop().toLowerCase();
    if (!ALLOWED_EXT.includes(ext)) {
      setErrors((p) => ({ ...p, file: `Allowed files: ${ALLOWED_EXT.join(", ")}` }));
      e.target.value = "";
      return;
    }
    if (f.size > MAX_MB * 1024 * 1024) {
      setErrors((p) => ({ ...p, file: `File must be smaller than ${MAX_MB} MB.` }));
      e.target.value = "";
      return;
    }
    setErrors((p) => ({ ...p, file: undefined }));
    setFile(f);
  };

  const removeFile = () => {
    setFile(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = {};
    if (!sender) errs.sender = "Company email is not configured.";
    if (!receiver) errs.receiver = "This student has no email address.";
    if (!title.trim()) errs.title = "Mail title is required.";
    if (!description.trim()) errs.description = "Mail description is required.";
    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }

    setErrors({});
    setServerError("");
    setSending(true);

    const fd = new FormData();
    fd.append("subject", title.trim());
    fd.append("message", description.trim());
    if (file) fd.append("attachment", file);

    try {
      await adminApi.post(`/admin/student-registrations/${student.reg_id}/send-mail`, fd);
      onSent?.(`Mail sent to ${receiver}`);
      onClose();
    } catch (err) {
      setServerError(
        err.response?.data?.message || "Could not send the mail. Please try again."
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={() => !sending && onClose()}
      className="fixed inset-0 z-[60] bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4"
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.97, y: 12 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.97, y: 12 }}
        transition={{ duration: 0.2 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 bg-gradient-to-r from-[#0B2545] to-[#143A63]">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-white/15 flex items-center justify-center">
              <Mail size={17} className="text-white" />
            </div>
            <div>
              <h4 className="font-semibold text-white text-[15px] leading-tight">Send Mail</h4>
              <p className="text-[11px] text-white/70">{student.st_name || "Student"}</p>
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            disabled={sending}
            title="Close"
            aria-label="Close"
            className="text-red-500 hover:text-red-600 transition disabled:opacity-40"
          >
            <X size={18} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} noValidate className="px-6 py-6 space-y-4">
          <Row label="Sender Mail" required error={errors.sender}>
            <input
              type="email"
              value={sender}
              readOnly
              placeholder="Company email not configured"
              className={readOnlyCls}
            />
          </Row>

          <Row label="Receiver Mail" required error={errors.receiver}>
            <input
              type="email"
              value={receiver}
              readOnly
              placeholder="No email address available"
              className={readOnlyCls}
            />
          </Row>

          <Row label="Mail Title" required error={errors.title}>
            <input
              type="text"
              value={title}
              maxLength={150}
              onChange={(e) => {
                setTitle(e.target.value);
                if (errors.title) setErrors((p) => ({ ...p, title: undefined }));
              }}
              placeholder="Enter mail title"
              className={`${inputCls} ${errors.title ? "border-red-400" : ""}`}
            />
          </Row>

          <Row label="Mail Description" required error={errors.description}>
            <textarea
              rows={7}
              value={description}
              onChange={(e) => {
                setDescription(e.target.value);
                if (errors.description) setErrors((p) => ({ ...p, description: undefined }));
              }}
              placeholder="Type your message here..."
              className={`${inputCls} resize-y ${errors.description ? "border-red-400" : ""}`}
            />
          </Row>

          <Row label="Attachment" error={errors.file}>
            <input
              ref={fileRef}
              type="file"
              accept={ALLOWED_EXT.join(",")}
              onChange={handleFile}
              className="hidden"
            />
            <div className="flex items-center gap-2 flex-wrap">
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                className="inline-flex items-center gap-1.5 text-xs font-medium border border-slate-300 rounded-lg px-3 py-2 bg-slate-50 hover:bg-slate-100 text-slate-700 transition"
              >
                <Paperclip size={14} />
                Choose File
              </button>

              <span className="text-xs text-slate-500 truncate max-w-[240px]">
                {file ? file.name : "No file chosen"}
              </span>

              {file && (
                <button
                  type="button"
                  onClick={removeFile}
                  title="Remove attachment"
                  className="text-red-500 hover:text-red-700 transition"
                >
                  <Trash2 size={14} />
                </button>
              )}
            </div>
            <p className="text-[11px] text-slate-400 mt-1">
              Optional · PDF, JPG, PNG, DOC, XLS · max {MAX_MB} MB
            </p>
          </Row>

          {serverError && (
            <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
              {serverError}
            </p>
          )}

          {/* Buttons */}
          <div className="grid grid-cols-1 sm:grid-cols-[150px_1fr] gap-x-4">
            <span className="hidden sm:block" />
            <div className="flex items-center gap-3 pt-1">
              <button
                type="button"
                onClick={onClose}
                disabled={sending}
                className="inline-flex items-center gap-1.5 px-6 py-2 rounded-lg border border-[#0B2545] text-[#0B2545] text-sm font-semibold hover:bg-[#0B2545]/5 transition disabled:opacity-50"
              >
                <ArrowLeft size={15} />
                Back
              </button>

              <button
                type="submit"
                disabled={sending || !receiver || !sender}
                className="inline-flex items-center gap-1.5 px-6 py-2 rounded-lg bg-[#0B2545] hover:bg-[#143A63] text-white text-sm font-semibold shadow-sm transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {sending ? (
                  <Loader2 size={15} className="animate-spin" />
                ) : (
                  <Send size={15} />
                )}
                {sending ? "Sending..." : "Submit"}
              </button>
            </div>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
}
